/**
 * 画布手势状态机 —— 纯逻辑，零 Vue / 零 DOM 依赖。
 *
 * ## 为什么要它
 *
 * 在抽出来之前，连线手势的状态是散在 Playground 里的 8 个 ref
 * （linkingNodeId / linkingHandleId / linkingType / linkingSide /
 *   pendingDetach / detachedEdge / lassoMode / lassoPath），
 * 碰它们的函数有 17 个，而它们之间的约束**只存在于作者脑子里**：
 *
 *   1. `detachedEdge` 非空 ⟹ 那条边**不在**图上
 *   2. `pendingDetach` 非空 ⟹ 那条边**还在**图上
 *   3. `pendingDetach` 与 `detachedEdge` 不会同时非空
 *   4. `linkingType` 非空 ⟺ 正在拖线
 *   5. `lassoMode` 非空 ⟹ 路径非空
 *   6. mouseup 监听已注册 ⟺ 有「待决的拖拽」
 *   7. 起点的 type 决定高亮哪一侧
 *
 * 第 6 条真的炸过 —— 清理函数图省事把 mouseup 一起摘了，
 * 于是「拖出去又拖回来松手」被当成落空，误删连线。
 *
 * 现在这些约束全部落进**状态类型**：`armedDetach`（边还在）与
 * `detached`（边已摘）是两个不同的状态，不可能同时成立。
 *
 * ## 设计要点
 *
 * - **不吃 DOM、不吃图引擎**：所有外部信息通过 `GestureContext` 注入。
 *   所以测试里可以喂假数据，把「按一下 → 拖开 → 拖回来 → 松手」这种
 *   时序问题在浏览器之外验掉。
 * - **只吐命令，不自己动手**：状态机说「该删这条边」，删不删是宿主的事。
 */
import type { Point } from './cutGeometry'

/* ══════════════════════════════════════════════════════════════
   基础类型
   ══════════════════════════════════════════════════════════════ */

export interface HandleRef {
  nodeId: string
  handleId: string
  /** 端口在节点上的角色 */
  type: 'source' | 'target'
}

export interface EdgeRef {
  id: string
  source: string
  sourceHandle: string | null
  target: string
  targetHandle: string | null
}

/* ══════════════════════════════════════════════════════════════
   状态
   ══════════════════════════════════════════════════════════════ */

export type GestureState =
  /** 什么都没发生 */
  | { kind: 'idle' }
  /**
   * 从某个端口起线中（不含「从已连线的输入拖出」那种）。
   *
   * `lastPoint` 是给「松手落空 → 弹候选菜单」用的：菜单要出现在鼠标那里，
   * 而 `connectEnd` 这个事件本身**不带坐标**。
   */
  | { kind: 'linking'; from: HandleRef; startedAt: Point; lastPoint: Point }
  /**
   * 从**已连接的输入端口**按下了，但还没真的拖开。
   *
   * 不变式：这条边**还在**图上。位移没超过阈值就不该动它 ——
   * 否则在端口上点一下就会把线删掉。
   */
  | {
      kind: 'armedDetach'
      edge: EdgeRef
      startedAt: Point
      /** 用来判定「真的拖开了」的位移阈值，屏幕像素 */
      threshold: number
    }
  /**
   * 已经把这根线从图上摘下来了。
   *
   * 不变式：这条边**不在**图上。松手时落到哪里，决定它是被放回、被改接，还是就此断开。
   */
  | { kind: 'detached'; edge: EdgeRef }
  /** 按住 Alt 正在划一条线（剪断 / 静音） */
  | { kind: 'lasso'; mode: LassoMode; path: Point[] }

export type LassoMode = 'cut' | 'mute'

/* ══════════════════════════════════════════════════════════════
   事件
   ══════════════════════════════════════════════════════════════ */

export type GestureEvent =
  /** 图引擎报「某个端口被按下，开始连线了」 */
  | {
      type: 'connectStart'
      nodeId?: string
      handleId: string | null
      handleType?: string
      point: Point
    }
  /** 图引擎真正建立了一条连接 */
  | {
      type: 'connect'
      source: string
      sourceHandle: string | null
      target: string
      targetHandle: string | null
    }
  /** 图引擎报「这次连线过程结束了」—— 无论成没成都会来 */
  | { type: 'connectEnd' }
  | { type: 'pointerMove'; point: Point; buttons?: number }
  | { type: 'pointerUp'; point: Point }
  | { type: 'pointerCancel' }
  /** 按住 Alt 起划 */
  | { type: 'lassoStart'; mode: LassoMode; point: Point }

/* ══════════════════════════════════════════════════════════════
   命令（状态机只提议，执行是宿主的事）
   ══════════════════════════════════════════════════════════════ */

export type GestureCommand =
  | { type: 'removeEdge'; edgeId: string }
  | {
      type: 'addEdge'
      source: string
      sourceHandle: string | null
      target: string
      targetHandle: string | null
    }
  /** 把连线的**视觉起点**挪到原输出端口（校验不受影响，见 README） */
  | { type: 'repointConnectionLine'; from: HandleRef }
  | { type: 'cutEdges'; edgeIds: string[] }
  | { type: 'setEdgeMuted'; edgeIds: string[]; muted: boolean }
  | { type: 'notice'; message: string; undoEdge?: EdgeRef }
  /**
   * 松手落空 → 请在鼠标位置列出「能接上的节点/端口」（Blender 的 link drag search）。
   *
   * 状态机不管候选有哪些 —— 那是宿主的节点目录才知道的事。
   */
  | {
      type: 'offerLinkCandidates'
      origin: HandleRef
      point: Point
      /**
       * 从已有输入拖出来时带上原边：
       * 菜单如果被直接关掉（没选任何东西），语义就是「断开」，宿主据此提示撤销。
       */
      undoEdge?: EdgeRef
    }

/* ══════════════════════════════════════════════════════════════
   注入的上下文
   ══════════════════════════════════════════════════════════════ */

export interface GestureContext {
  /** 这条输入端口上挂着哪条边 */
  edgeOnInput(nodeId: string, handleId: string): EdgeRef | undefined
  /** 松手时指针底下是哪个端口（DOM 查询，宿主提供） */
  handleAt(point: Point): HandleRef | null
  /** 目标端口能不能接收来自这个起点的连线 */
  canDrop(from: HandleRef, to: HandleRef): boolean
  /** 剪线 / 静音：这条划痕扫到了哪些边 */
  hitTest(path: readonly Point[]): string[]
  /** 某条边当前是否静音 */
  isEdgeMuted(edgeId: string): boolean
}

export interface GestureOptions {
  context: GestureContext
  /** 「真的拖开了」的位移阈值，默认 4px */
  detachThreshold?: number
}

const DEFAULT_DETACH_THRESHOLD = 4

/**
 * 小于这个距离就当「**点击**」而不是「拖动」（像素）。
 *
 * 用在「从端口拖出线、松手落在空白 → 弹候选菜单」这个判断上：
 * `connectOnClick` 默认是开的，点一下端口也会进 linking ——
 * 不加这个判断的话，最常见的「点端口看看」会糊一脸菜单。
 */
export const CLICK_SLOP = 8

/* ══════════════════════════════════════════════════════════════
   派生查询（宿主渲染高亮时用）
   ══════════════════════════════════════════════════════════════ */

/**
 * 这次手势的**逻辑起点** —— 高亮与落点校验都按它算。
 *
 * 「从已连线的输入端口拖出」时，起点是**原输出端口**而不是被按的那个输入：
 * 视觉上那根线本来就从它画出来，语义上要重接的也是它。
 */
export function gestureOrigin(state: GestureState): HandleRef | null {
  if (state.kind === 'linking') return state.from
  if (state.kind === 'armedDetach' || state.kind === 'detached') {
    return {
      nodeId: state.edge.source,
      handleId: state.edge.sourceHandle ?? '',
      type: 'source',
    }
  }
  return null
}

/** 高亮哪一侧：起点是输出 → 亮输入；起点是输入 → 亮输出 */
export function gestureWantsInputs(state: GestureState): boolean {
  return gestureOrigin(state)?.type !== 'target'
}

/** 正在拖线吗（宿主用它切端口的显示状态） */
export function isLinking(state: GestureState): boolean {
  return (
    state.kind === 'linking' ||
    state.kind === 'armedDetach' ||
    state.kind === 'detached'
  )
}

/** 正在划痕吗 */
export function isLassoing(state: GestureState): boolean {
  return state.kind === 'lasso'
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/* ══════════════════════════════════════════════════════════════
   归约
   ══════════════════════════════════════════════════════════════ */

export interface Transition {
  state: GestureState
  commands: GestureCommand[]
}

export function initialGestureState(): GestureState {
  return { kind: 'idle' }
}

export function reduce(
  state: GestureState,
  event: GestureEvent,
  options: GestureOptions,
): Transition {
  const { context } = options
  const threshold = options.detachThreshold ?? DEFAULT_DETACH_THRESHOLD

  const stay = (commands: GestureCommand[] = []): Transition => ({
    state,
    commands,
  })
  const go = (
    next: GestureState,
    commands: GestureCommand[] = [],
  ): Transition => ({ state: next, commands })

  switch (event.type) {
    /* ── 起线 ─────────────────────────────────────────────── */
    case 'connectStart': {
      if (state.kind !== 'idle') return stay()
      const { nodeId, handleId, handleType, point } = event
      if (!nodeId || !handleId) return stay()

      // 从**已连接的输入端口**按下 → 先挂起，等真的拖开了再摘线
      if (handleType === 'target') {
        const edge = context.edgeOnInput(nodeId, handleId)
        if (edge) {
          const from: HandleRef = {
            nodeId: edge.source,
            handleId: edge.sourceHandle ?? '',
            type: 'source',
          }
          return go(
            { kind: 'armedDetach', edge, startedAt: point, threshold },
            // 立刻把连线视觉重指到原输出端口（校验不受影响）
            [{ type: 'repointConnectionLine', from }],
          )
        }
      }

      return go({
        kind: 'linking',
        from: {
          nodeId,
          handleId,
          type: handleType === 'target' ? 'target' : 'source',
        },
        startedAt: point,
        lastPoint: point,
      })
    }

    /* ── 图引擎建立了连线 ─────────────────────────────────── */
    case 'connect': {
      const command: GestureCommand = {
        type: 'addEdge',
        source: event.source,
        sourceHandle: event.sourceHandle,
        target: event.target,
        targetHandle: event.targetHandle,
      }
      // linking：正常的拖拽连线
      // detached：从输入拖出、落到输出端口上，图引擎自己接上了（Strict 下合法）
      if (state.kind === 'linking' || state.kind === 'detached') {
        return go({ kind: 'idle' }, [command])
      }
      return stay([command])
    }

    /* ── 连线过程结束 ─────────────────────────────────────── */
    case 'connectEnd': {
      if (state.kind === 'detached') {
        // 摘下来了又没接上 → 就此断开，但给一次撤销机会
        const { edge } = state
        return go({ kind: 'idle' }, [
          {
            type: 'notice',
            message: `已断开：${edge.source}.${edge.sourceHandle} → ${edge.target}.${edge.targetHandle}`,
            undoEdge: edge,
          },
        ])
      }
      if (state.kind === 'linking') {
        /*
         * ★ 走到这里说明**图引擎没有建立连接**（接上了的话状态已经是 idle）。
         * 也就是「拖了根线出来、松手落在空白处」—— Blender 在这里弹候选菜单，
         * 让用户直接搜一个节点、建出来、连上。
         */
        const origin = gestureOrigin(state)
        if (!origin) return go({ kind: 'idle' })

        /*
         * ★★ 只有**真的拖动过**才弹候选菜单。
         *
         * `connectOnClick` 默认是开的 —— 点一下端口也会进 linking。
         * 不加这个距离判断的话，「点端口看看」= 松手糊一脸节点菜单，
         * 而这恰恰是最常见的操作。
         *
         * 触摸端也一样受益：点端口 → 点目标端口 两次点击就能连线，
         * 中间不该弹出菜单挡路。
         */
        if (distance(state.lastPoint, state.startedAt) < CLICK_SLOP) {
          return go({ kind: 'idle' })
        }

        return go({ kind: 'idle' }, [
          { type: 'offerLinkCandidates', origin, point: state.lastPoint },
        ])
      }
      if (state.kind === 'armedDetach') return go({ kind: 'idle' })
      return stay()
    }

    /* ── 指针移动 ─────────────────────────────────────────── */
    case 'pointerMove': {
      if (state.kind === 'armedDetach') {
        // 主键松了就当作取消 —— connectOnClick 默认为 true，
        // 「点一下端口再挪鼠标」不该被当成拖拽
        if (event.buttons === 0) return go({ kind: 'idle' })
        if (distance(event.point, state.startedAt) < state.threshold) {
          return stay()
        }
        // ★ 真的拖开了：到这一刻才把线从图上摘掉
        return go({ kind: 'detached', edge: state.edge }, [
          { type: 'removeEdge', edgeId: state.edge.id },
        ])
      }
      if (state.kind === 'linking') {
        // 只更新落点，不改变状态 —— 连线过程还是图引擎在管
        return go({ ...state, lastPoint: event.point })
      }
      if (state.kind === 'lasso') {
        return go({ kind: 'lasso', mode: state.mode, path: [...state.path, event.point] })
      }
      return stay()
    }

    /* ── 指针松开 ─────────────────────────────────────────── */
    case 'pointerUp': {
      if (state.kind === 'armedDetach') {
        // 按了但没拖动 → 什么都不做（绝不能因为点一下就删线）
        return go({ kind: 'idle' })
      }

      if (state.kind === 'lasso') {
        const path = state.path
        if (path.length === 0) return go({ kind: 'idle' })
        const edgeIds = context.hitTest(path)
        if (edgeIds.length === 0) {
          return go({ kind: 'idle' }, [{ type: 'notice', message: '没有划到任何连线' }])
        }
        if (state.mode === 'cut') {
          return go({ kind: 'idle' }, [
            { type: 'cutEdges', edgeIds },
            { type: 'notice', message: `已剪断 ${edgeIds.length} 条连线` },
          ])
        }
        // 静音：以第一条为准统一取反，行为可预期
        const muted = !context.isEdgeMuted(edgeIds[0] as string)
        return go({ kind: 'idle' }, [
          { type: 'setEdgeMuted', edgeIds, muted },
          {
            type: 'notice',
            message: muted
              ? `已静音 ${edgeIds.length} 条连线`
              : `已取消静音 ${edgeIds.length} 条连线`,
          },
        ])
      }

      if (state.kind === 'detached') {
        const { edge } = state
        const below = context.handleAt(event.point)

        // 落回原来那个输入端口 → 视为「这次拖拽取消了」，放回去
        if (
          below &&
          below.nodeId === edge.target &&
          below.handleId === (edge.targetHandle ?? '')
        ) {
          return go({ kind: 'idle' }, [
            {
              type: 'addEdge',
              source: edge.source,
              sourceHandle: edge.sourceHandle,
              target: edge.target,
              targetHandle: edge.targetHandle,
            },
          ])
        }

        const origin = gestureOrigin(state)
        // 落到另一个输入端口且合法 → 自己接手改接
        // （Strict 模式下 target→target 图引擎判为非法，不会触发 connect）
        /*
         * ⚠️ 这里原来还有一个 `below.nodeId !== edge.target` 的条件。
         * 它是**多余而且有害**的：
         *   · 「落回原来那个端口」已经被上面那个分支接走了；
         *   · 于是剩下的同节点情况只可能是**同一个节点上的另一个输入端口** ——
         *     而把线从 `content` 改接到 `fontColor` 是完全正常的操作。
         * 原来的写法会把它推进下面的「落空」分支，表现就是
         * 「拖到另一个可以接的端口上，只留下一条预览线、什么也没接上」。
         */
        if (below && origin && below.type === 'target' && context.canDrop(origin, below)) {
          return go({ kind: 'idle' }, [
            {
              type: 'addEdge',
              source: origin.nodeId,
              sourceHandle: origin.handleId,
              target: below.nodeId,
              targetHandle: below.handleId,
            },
          ])
        }

        /*
         * ★ 开发期诊断：走到这里说明「指针底下确实有个输入端口，但没接上」。
         * 不打出原因的话，用户看到的只是「预览线还在、什么都没发生」，
         * 而可能的原因有四种（端口没识别出来 / 类型不兼容 / 成环 / 端口查不到），
         * 从现象上完全区分不开。只在失败路径上打，正常路径零开销。
         */
        if (below && import.meta.env?.DEV !== false) {
          console.warn(
            '[node-print] 拔线后落到端口上但没接成：',
            `${origin?.nodeId ?? '?'}.${origin?.handleId ?? '?'} → ${below.nodeId}.${below.handleId}`,
            '\n  落点类型:', below.type,
            '\n  结构校验:', context.canDrop(origin ?? { nodeId: '', handleId: '', type: 'source' }, below),
            '\n  原边:', `${edge.source}.${edge.sourceHandle} → ${edge.target}.${edge.targetHandle}`,
          )
        }

        /*
         * 落空（空白处 / 非法落点）。
         *
         * ★ 不是简单「断开」，而是弹候选菜单（Blender 行为）：
         * 用户拽下一根线又落在空处，最常见的意图是「换个东西接上」，
         * 而不是「我就是想删掉它」。
         * 所以把原边一并交给宿主 —— 菜单没选任何东西时，才提示「已断开 + 撤销」。
         */
        if (!below || below.type === 'target') {
          const origin = gestureOrigin(state)
          return go({ kind: 'idle' }, [
            {
              type: 'offerLinkCandidates',
              origin: origin ?? {
                nodeId: edge.source,
                handleId: edge.sourceHandle ?? '',
                type: 'source',
              },
              point: event.point,
              undoEdge: edge,
            },
          ])
        }

        /*
         * 只剩一种：落到**输出端口**上。
         *
         * Strict 模式下 target→source 是合法的，图引擎可能自己把它接上 ——
         * 那样会先来 `connect`、再来 `connectEnd`。所以留在这里等：
         *   connect    → 接上了
         *   connectEnd → 没接上，按断开处理
         */
        return stay()
      }

      return stay()
    }

    /* ── 指针被系统取消 ───────────────────────────────────── */
    case 'pointerCancel': {
      if (state.kind === 'idle') return stay()
      // 取消 ≠ 落空：把线放回去，不要当成断开
      if (state.kind === 'detached') {
        const { edge } = state
        return go({ kind: 'idle' }, [
          {
            type: 'addEdge',
            source: edge.source,
            sourceHandle: edge.sourceHandle,
            target: edge.target,
            targetHandle: edge.targetHandle,
          },
        ])
      }
      return go({ kind: 'idle' })
    }

    /* ── 起划 ─────────────────────────────────────────────── */
    case 'lassoStart': {
      if (state.kind !== 'idle') return stay()
      return go({ kind: 'lasso', mode: event.mode, path: [event.point] })
    }

    default:
      return stay()
  }
}
